import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/helpers";
import MasLogo from "../components/MasLogo";

function AuthPanel() {
  return (
    <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 rounded-3xl relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-72 h-72 bg-[#0A0A0A]/25 rounded-full blur-[100px]" />
      <div className="absolute -bottom-20 -left-10 w-72 h-72 bg-[#D4AF37]/15 rounded-full blur-[100px]" />
      <MasLogo tone="light" size="lg" className="self-start" />
      <div className="relative">
        <h2 className="font-display font-extrabold text-4xl tracking-tighter leading-tight">The new standard in grading.</h2>
        <p className="text-white/70 mt-4 max-w-sm">Track submissions, verify certs, and manage your collection — all in one place.</p>
        <div className="flex items-center gap-2 mt-8 text-sm text-white/60"><ShieldCheck size={18} className="text-green-400" /> Bank-grade encrypted checkout</div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back");
      nav(next || (user.role === "admin" ? "/admin" : user.role === "staff" ? "/convention" : "/portal"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const input = "w-full mt-2 bg-white border border-slate-200 rounded-xl px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/30 outline-none transition-all duration-200";

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
      <div className="grid lg:grid-cols-2 gap-8 items-stretch">
        <div className="py-6">
          <h1 className="font-display font-extrabold text-4xl tracking-tighter text-[#0A0A0A]">Sign In</h1>
          <p className="text-slate-600 mt-2 mb-8">Access your submissions and grading portal.</p>
          <form onSubmit={submit} className="space-y-4 max-w-md">
            <div><label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Email</label><input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={input} /></div>
            <div><label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Password</label><input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={input} /></div>
            <button data-testid="login-submit" disabled={loading} className="w-full py-4 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-[#171717] transition-colors duration-300 disabled:opacity-60">{loading ? "Signing in…" : "Sign In"}</button>
          </form>
          <p className="text-slate-600 text-sm mt-6">No account? <Link to={next ? `/register?next=${encodeURIComponent(next)}` : "/register"} className="font-bold text-[#0A0A0A] hover:underline">Create one</Link></p>
        </div>
        <AuthPanel />
      </div>
    </div>
  );
}
